import React, { useState, useEffect } from 'react';
import { useStacks } from '@/hooks/useStacks';
import { useFeedTypes } from '@/hooks/useFeedTypes';
import { feedingService } from '@/services/feedingService';

interface QuickAction {
  id: string;
  title: string;
  icon: string;
  hotkey: string;
  color: string;
}

interface QuickInputIntegrationProps {
  children: React.ReactNode;
}

const quickActions: QuickAction[] = [
  {
    id: 'feed',
    title: 'Покормить',
    icon: '🥗',
    hotkey: 'Alt+F',
    color: 'bg-green-500 hover:bg-green-600'
  },
  {
    id: 'measure',
    title: 'Замерить',
    icon: '🌡️',
    hotkey: 'Alt+M',
    color: 'bg-blue-500 hover:bg-blue-600'
  },
  {
    id: 'harvest',
    title: 'Биогумус',
    icon: '🌱',
    hotkey: 'Alt+H',
    color: 'bg-amber-500 hover:bg-amber-600'
  },
  {
    id: 'voice',
    title: 'Голос',
    icon: '🎤',
    hotkey: 'Alt+V',
    color: 'bg-purple-500 hover:bg-purple-600'
  }
];

const QuickInputIntegration: React.FC<QuickInputIntegrationProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const [selectedStack, setSelectedStack] = useState<number | null>(null);
  const [amount, setAmount] = useState<number>(1.5);
  const [selectedFeedType, setSelectedFeedType] = useState<number | null>(null);
  const [showHotkeysHint, setShowHotkeysHint] = useState(false);
  
  const { stacks } = useStacks();
  const { feedTypes } = useFeedTypes();

  // Обработка горячих клавиш
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) {
        switch (event.key.toLowerCase()) {
          case 'f':
            event.preventDefault();
            handleQuickAction('feed');
            break;
          case 'm':
            event.preventDefault();
            handleQuickAction('measure');
            break;
          case 'h':
            event.preventDefault();
            handleQuickAction('harvest');
            break;
          case 'v':
            event.preventDefault();
            handleQuickAction('voice');
            break;
        }
      }
      
      if (event.key === 'Escape' && (isOpen || activeAction)) {
        event.preventDefault();
        setIsOpen(false);
        setActiveAction(null);
      }

      if (event.key === 'F1') {
        event.preventDefault();
        setShowHotkeysHint(!showHotkeysHint);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeAction, showHotkeysHint]);

  const handleQuickAction = (actionId: string) => {
    setActiveAction(actionId);
    setIsOpen(true);
    
    if (actionId === 'feed' && feedTypes.length > 0) {
      // Автовыбор отрубей как наиболее частого корма
      const defaultFeed = feedTypes.find(f => f.name.includes('Отруби')) || feedTypes[0];
      setSelectedFeedType(defaultFeed.id);
    }
  };

  const handleQuickFeed = async () => {
    if (!selectedStack || !selectedFeedType) {
      alert('Выберите стеллаж и тип корма');
      return;
    }

    try {
      await feedingService.createFeedingRecord({
        stack_id: selectedStack,
        feed_type_id: selectedFeedType,
        amount_kg: amount,
        consumption_activity: 'medium'
      });
      
      alert('Кормление записано!');
      setIsOpen(false);
      setActiveAction(null);
    } catch (error) {
      alert('Ошибка записи кормления');
    }
  };

  const renderQuickFeedForm = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Быстрое кормление</h3>
      
      {/* Выбор стеллажа */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Стеллаж
        </label>
        <select
          value={selectedStack || ''}
          onChange={(e) => setSelectedStack(Number(e.target.value))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Выберите стеллаж</option>
          {stacks.map(stack => (
            <option key={stack.id} value={stack.id}>
              {stack.name}
            </option>
          ))}
        </select>
      </div>

      {/* Выбор корма */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Тип корма
        </label>
        <select
          value={selectedFeedType || ''}
          onChange={(e) => setSelectedFeedType(Number(e.target.value))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
        >
          <option value="">Выберите корм</option>
          {feedTypes.map(feed => (
            <option key={feed.id} value={feed.id}>
              {feed.name}
            </option>
          ))}
        </select>
      </div>

      {/* Количество с пресетами */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Количество (кг)
        </label>
        <div className="flex space-x-2 mb-2">
          {[0.5, 1.0, 1.5, 2.0].map(preset => (
            <button
              key={preset}
              onClick={() => setAmount(preset)}
              className={`px-3 py-1 rounded-lg text-sm ${
                amount === preset 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {preset} кг
            </button>
          ))}
        </div>
        <input
          type="number"
          step="0.1"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
        />
      </div>

      {/* Кнопки */}
      <div className="flex space-x-3">
        <button
          onClick={handleQuickFeed}
          className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          💾 Записать кормление
        </button>
        <button
          onClick={() => setIsOpen(false)}
          className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
        >
          Отмена
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeAction) {
      case 'feed':
        return renderQuickFeedForm();
      case 'measure':
        return <div>Форма замеров (в разработке)</div>;
      case 'harvest':
        return <div>Форма сбора биогумуса (в разработке)</div>;
      case 'voice':
        return <div>Голосовые команды (в разработке)</div>;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Основной контент страницы - ВАЖНО! */}
      {children}
      
      {/* Плавающие кнопки быстрых действий */}
      <div className="fixed bottom-6 right-6 space-y-3 z-30">
        {quickActions.map(action => (
          <button
            key={action.id}
            onClick={() => handleQuickAction(action.id)}
            className={`w-14 h-14 ${action.color} text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110`}
            title={`${action.title} (${action.hotkey})`}
          >
            <span className="text-xl">{action.icon}</span>
          </button>
        ))}
      </div>

      {/* Модальное окно быстрого ввода */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            {renderContent()}
          </div>
        </div>
      )}

      {/* Подсказка по горячим клавишам (показывается по F1) */}
      {showHotkeysHint && (
        <div className="fixed bottom-6 left-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 text-xs text-gray-600 dark:text-gray-300 z-20 border border-gray-200 dark:border-gray-600 lg:left-72 max-lg:left-6">
          <div className="flex justify-between items-center mb-2">
            <div className="font-medium text-sm">Горячие клавиши:</div>
            <button
              onClick={() => setShowHotkeysHint(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm"
            >
              ✕
            </button>
          </div>
          <div className="space-y-1">
            <div>Alt+F - Кормление</div>
            <div>Alt+M - Замеры</div>
            <div>Alt+H - Биогумус</div>
            <div>Alt+V - Голос</div>
            <div>F1 - Показать/скрыть подсказку</div>
            <div>Esc - Закрыть</div>
          </div>
        </div>
      )}

      {/* Кнопка для показа горячих клавиш */}
      <button
        onClick={() => setShowHotkeysHint(!showHotkeysHint)}
        className="fixed bottom-6 left-72 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full w-10 h-10 flex items-center justify-center shadow-lg z-20 transition-colors lg:left-72 max-lg:left-6"
        title="Показать горячие клавиши (F1)"
      >
        ❓
      </button>
    </>
  );
};

export default QuickInputIntegration;